'use client'

/**
 * Shared client component for per-segment Next.js App Router error
 * boundaries (Issue #11651).
 *
 * `src/components/error-boundary-card.tsx` (#5466) already owns all the
 * presentational chrome (icon bubble, heading, action row, copy-to-
 * clipboard error details, the single `role="alert"` live region). What
 * was triplicated across `src/app/error.tsx`, `src/app/admin/error.tsx`,
 * and `src/app/people/error.tsx` before this component existed is the
 * *wiring*: the props interface, the reporting `useEffect`, and the
 * `onCopyFailure` toast. This component owns that wiring exactly once so
 * every top-level route segment's `error.tsx` can be a ~10-line delegate:
 *
 *   'use client'
 *   import { RouteErrorBoundary } from '@/components/route-error-boundary'
 *
 *   export default function MeetingsError(props: {
 *     error: Error & { digest?: string }
 *     reset: () => void
 *   }) {
 *     return <RouteErrorBoundary segment="meetings" {...props} />
 *   }
 *
 * Reporting decisions (see task list / prompt §C for the full reasoning):
 *
 * 1. `errorId` — a single generic `ERROR_IDS.ROUTE_ERROR_BOUNDARY` is used
 *    for every segment. Segment discrimination happens via `action` and
 *    `context`, not via one ERROR_IDS member per segment.
 * 2. The dedupe key — `reportClientError` dedupes on
 *    `errorId:action:message` for 1s, and `context` is NOT part of that
 *    key. A constant `action` across all segments would silently collapse
 *    two different segments failing with the same message inside the
 *    same second into a single report — a real telemetry loss. `action`
 *    therefore always carries the segment (`routeError:${segment}`), so
 *    the dedupe window is scoped per segment.
 * 3. Every report consolidates onto `reportClientError`'s `'caught-error'`
 *    source (the house catch-block path, #6511) — there is no escape
 *    hatch to a different `logClientError` source; add one only if a
 *    real consumer needs it.
 */

import { type ReactNode, useEffect } from 'react'
import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { ErrorBoundaryCard } from '@/components/error-boundary-card'
import {
  isChunkLoadError,
  reloadOncePerDeployment,
} from '@/lib/chunk-load-recovery'
import { reportClientError } from '@/lib/client-error'
import { ERROR_IDS } from '@/lib/errors/constants'
import {
  clearRouteErrorActive,
  publishRouteErrorActive,
} from '@/lib/errors/route-error-posture'
import { safeRefresh } from '@/lib/safe-refresh'
import { toastError } from '@/lib/toast'

type Tone = 'red' | 'amber'

const DEFAULT_TITLE = 'Something went wrong'
const DEFAULT_MESSAGE = 'An unexpected error occurred while loading this page.'
const DEFAULT_HOME_HREF: Route = '/today'
const DEFAULT_HOME_LABEL = 'Go home'
const DEFAULT_TONE: Tone = 'red'

/**
 * Copy shown when React itself reports a hooks-count mismatch (typically
 * caused by an in-flight navigation unmounting a component mid-render).
 * This is not admin-specific — a hooks-count mismatch can occur in any
 * segment — so the special case that used to live only in
 * `src/app/admin/error.tsx` is promoted here to benefit every segment.
 */
const HOOKS_MISMATCH_MESSAGE =
  'A rendering error occurred during navigation. This is usually temporary.'

/**
 * Copy for a chunk-load error (#15676): the page asked for a build chunk the
 * server no longer has, almost always because a deploy rolled out while the
 * tab was open. `reset()` re-renders against the same stale client and fails
 * again, so the only useful action is a full reload.
 */
const CHUNK_LOAD_TITLE = 'A new version is available'
const CHUNK_LOAD_MESSAGE =
  'This app was updated while the page was open. Reload the page to get the latest version.'
const CHUNK_LOAD_RESET_LABEL = 'Reload page'

function isHooksCountMismatch(error: Error): boolean {
  return (
    error.message?.includes('Rendered more hooks') ||
    error.message?.includes('Rendered fewer hooks')
  )
}

interface SegmentBoundaryCopy {
  title?: string
  message?: ReactNode
  homeHref?: Route
  homeLabel?: string
  tone?: Tone
}

/**
 * Sign-in-safe home destination shared by every segment that can be
 * reached by a user who is not (or no longer) in an authenticated app
 * session — `auth`, `no-app-access`, `trial-expired`, `unauthorized`. If
 * the boundary in one of these segments fell back to `DEFAULT_HOME_HREF`
 * (`/today`), "Go home" would route straight back into the authenticated
 * app shell for a user who, by construction, cannot get past it — see
 * `src/app/trial-expired/page.tsx` and `src/app/auth/not-in-directory/
 * page.tsx`, which already link their own "try again" actions to this
 * exact route rather than an authenticated one. Tracked in
 * `tests/unit/components/route-error-boundary.test.tsx` ("sign-in-safe
 * homeHref").
 */
const SIGN_IN_SAFE_HOME_HREF: Route = '/auth/signin'
const SIGN_IN_SAFE_HOME_LABEL = 'Back to Sign In'

/**
 * Acronym / meaning overrides for `humanizeSegmentTitle` below — kept to the
 * cases where naive per-word capitalization would either look wrong (`Ai`
 * instead of `AI`, `Actionboard` instead of the product's own `ActionBoard`)
 * or lose the segment's actual meaning (`ds` is short for "Design System",
 * the internal component-showcase route).
 */
const SEGMENT_TITLE_OVERRIDES: Record<string, string> = {
  actionboard: 'ActionBoard',
  ai: 'AI',
  ds: 'Design System',
  whats: 'What\u2019s',
}

/**
 * kebab-case segment identifier -> human-readable Title Case label, used to
 * build the generic "Something went wrong loading <Segment>" title for
 * segments that don't need bespoke copy (see `GENERIC_COPY_SEGMENTS` below).
 */
function humanizeSegmentTitle(segment: string): string {
  return segment
    .split('-')
    .map(
      word =>
        SEGMENT_TITLE_OVERRIDES[word] ??
        `${word.charAt(0).toUpperCase()}${word.slice(1)}`,
    )
    .join(' ')
}

/**
 * Segments that render pages but don't need bespoke error copy beyond their
 * own name — every per-segment `error.tsx` added in Issue #11651 task 11
 * except the four sign-in-safe segments seeded in task 7 (`auth`,
 * `no-app-access`, `trial-expired`, `unauthorized`, below) and the three
 * pre-existing boundaries refactored in task 8 (`root`, `admin`, `people`,
 * also below). Each gets a generated `{ title: 'Something went wrong
 * loading <Segment>' }` entry instead of 31 hand-written near-duplicates;
 * `message`/`homeHref`/`homeLabel`/`tone` fall through to `DEFAULT_*`.
 */
const GENERIC_COPY_SEGMENTS = [
  // Issue #12469 — flag-gated ActionBoard route.
  'actionboard',
  'ai-integrations',
  'boards',
  'company-info',
  'component-tests',
  // #16759 — `/decisions/[id]`, one decision's page.
  'decisions',
  'dev',
  'ds',
  // #14243 — `/events`, the full upcoming list beside `/notes`.
  'events',
  'feedback',
  'groups',
  // #16117 — the org page, renamed from `/handbook`.
  'handbook',
  // Issue #13391 — flag-gated chat-first Home route.
  'home',
  // #16980 — `/issues/[id]`, one topic's or issue's page.
  'issues',
  'markdown',
  'me',
  'measurables',
  'meetings',
  // #14241 — `/notes/[id]` and its transcript; #14243 — the `/notes` home.
  'notes',
  'notifications',
  'patterns',
  'performance',
  // Issue #13570 — the adopted-strategic-plan reading surface. A SEPARATE
  // segment from `plans` directly below, which is the quarterly Rock/Project
  // planning product: the two share a noun and nothing else, and a reader who
  // hit an error reading the district's plan should not be told the planning
  // session failed.
  'plan',
  'plans',
  'priorities',
  'projects',
  'proposals',
  'pulse',
  'reviews',
  // #15425 — `/rounds/[id]`, a rating round.
  'rounds',
  'scorecard',
  'settings',
  'surveys',
  'team',
  // #14302 — `/templates`, output templates built from a form.
  'templates',
  'today',
  'training',
  // #14619 — `/whats-new`, the viewer's announcement history.
  'whats-new',
  'work',
] as const

const GENERIC_SEGMENT_COPY: Record<string, SegmentBoundaryCopy> =
  Object.fromEntries(
    GENERIC_COPY_SEGMENTS.map(segment => [
      segment,
      {
        title: `Something went wrong loading ${humanizeSegmentTitle(segment)}`,
      },
    ]),
  )

/**
 * Per-segment copy overrides, keyed by the `segment` identifier passed to
 * `RouteErrorBoundary`. Populated by `GENERIC_SEGMENT_COPY` above (the 31
 * plain-generic segments) plus the seven segments below that need pinned,
 * hand-written copy: four sign-in-safe segments (`homeHref` is a
 * correctness requirement — see `SIGN_IN_SAFE_HOME_HREF` doc above) and the
 * three pre-existing boundaries refactored onto this component. Segments
 * absent from this record entirely fall back to the `DEFAULT_*` constants
 * below. A `RouteErrorBoundary` prop always wins over this record, which in
 * turn wins over the defaults — see the resolution order in the component
 * body.
 */
const SEGMENT_BOUNDARY_COPY: Record<string, SegmentBoundaryCopy> = {
  ...GENERIC_SEGMENT_COPY,
  auth: {
    homeHref: SIGN_IN_SAFE_HOME_HREF,
    homeLabel: SIGN_IN_SAFE_HOME_LABEL,
  },
  'no-app-access': {
    homeHref: SIGN_IN_SAFE_HOME_HREF,
    homeLabel: SIGN_IN_SAFE_HOME_LABEL,
  },
  'trial-expired': {
    homeHref: SIGN_IN_SAFE_HOME_HREF,
    homeLabel: SIGN_IN_SAFE_HOME_LABEL,
  },
  unauthorized: {
    homeHref: SIGN_IN_SAFE_HOME_HREF,
    homeLabel: SIGN_IN_SAFE_HOME_LABEL,
  },
  // Refactored onto RouteErrorBoundary in Issue #11651 (task 8). Copy
  // pinned explicitly (rather than left to DEFAULT_*) so a future change
  // to the shared defaults can't silently alter these three pre-existing
  // boundaries' user-facing text.
  root: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. This is usually temporary.',
    homeHref: '/today',
    homeLabel: 'Go home',
    tone: 'red',
  },
  admin: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred while loading this page.',
    homeHref: '/admin',
    homeLabel: 'Back to Admin',
    tone: 'amber',
  },
  people: {
    title: "Couldn't load this profile",
    message:
      "Something went wrong rendering this person's page. This is usually temporary.",
    homeHref: '/people',
    homeLabel: 'Back to People',
    tone: 'red',
  },
}

export interface RouteErrorBoundaryProps {
  /** Per-segment identifier, e.g. `'meetings'`. Threaded into telemetry
   * (`action`, `context`) and used to look up `SEGMENT_BOUNDARY_COPY`. */
  segment: string
  error: Error & { digest?: string }
  reset: () => void
  /** Per-segment copy override. Wins over `SEGMENT_BOUNDARY_COPY`. */
  title?: string
  message?: ReactNode
  homeHref?: Route
  homeLabel?: string
  tone?: Tone
  /**
   * Whether to fire the `reportClientError` telemetry side effect on
   * mount. Defaults to `true`, so all real production `error.tsx` route
   * segments are unaffected without needing to pass anything. Set to
   * `false` only for non-production mounts that must not emit real
   * telemetry — e.g. the `component-tests` showcase page, which mounts
   * this component purely for visual/interaction demonstration and has
   * no runtime gate keeping it out of production.
   */
  reportOnMount?: boolean
  /**
   * Whether to publish the route-error posture while this boundary is
   * mounted, which takes the root layout's floating chrome — the AI chat FAB
   * and panel, and the recording bar unless it is mid-capture — off screen
   * (#13956). Defaults to `true`, so every real `error.tsx` segment gets it
   * without passing anything.
   *
   * A SEPARATE decision from {@link reportOnMount}, even though the same
   * caller turns both off: one is about telemetry, the other about what the
   * viewer sees. The `component-tests` showcase sets this to `false` because
   * it mounts the card inside a page that is otherwise working, and blanking
   * that page's own chrome would be a component library breaking the app to
   * demonstrate a component.
   */
  suppressChrome?: boolean
}

export function RouteErrorBoundary({
  segment,
  error,
  reset,
  title,
  message,
  homeHref,
  homeLabel,
  tone,
  reportOnMount = true,
  suppressChrome = true,
}: RouteErrorBoundaryProps) {
  const router = useRouter()

  // The root layout's floating chrome is mounted ABOVE this subtree, so it
  // cannot see the failure; the store is the only channel to it. Published
  // for as long as this boundary is mounted and cleared on unmount, so "Try
  // again" / "Go home" bring the chrome back with the page.
  //
  // Keyed on `segment` as well as `suppressChrome` because a client-side
  // navigation can remount this component with a different segment before
  // the previous cleanup runs; publish and clear then name the same segment,
  // and the store's per-segment count absorbs the overlap.
  useEffect(() => {
    if (!suppressChrome) return

    publishRouteErrorActive(segment)
    return () => clearRouteErrorActive(segment)
  }, [segment, suppressChrome])

  useEffect(() => {
    if (!reportOnMount) return

    // The dedupe key inside reportClientError is `errorId:action:message`
    // for a 1s window, and `context` is NOT part of that key. A constant
    // `action` shared across every segment would silently collapse two
    // different segments failing with the same message inside the same
    // second into a single report — so `action` always carries the
    // segment to keep the dedupe window scoped per segment.
    reportClientError({
      errorId: ERROR_IDS.ROUTE_ERROR_BOUNDARY,
      action: `routeError:${segment}`,
      error,
      context: { segment },
    })
  }, [error, segment, reportOnMount])

  const isChunkError = isChunkLoadError(
    error,
    typeof window === 'undefined' ? undefined : window.location.origin,
  )

  // A chunk-load error caught here never reaches the window listeners
  // `installChunkLoadRecovery` registers, so the boundary runs the same
  // per-deployment reload itself (#15676). When the guard refuses (this
  // deployment already reloaded, or storage is unavailable) the card below
  // offers the reload by hand instead.
  useEffect(() => {
    if (isChunkError) reloadOncePerDeployment()
  }, [isChunkError])

  const segmentCopy = SEGMENT_BOUNDARY_COPY[segment] ?? {}
  const isHooksError = isHooksCountMismatch(error)

  const resolvedTitle = isChunkError
    ? CHUNK_LOAD_TITLE
    : (title ?? segmentCopy.title ?? DEFAULT_TITLE)
  const resolvedMessage = isChunkError
    ? CHUNK_LOAD_MESSAGE
    : (message ??
      (isHooksError
        ? HOOKS_MISMATCH_MESSAGE
        : (segmentCopy.message ?? DEFAULT_MESSAGE)))
  const resolvedHomeHref = homeHref ?? segmentCopy.homeHref ?? DEFAULT_HOME_HREF
  const resolvedHomeLabel =
    homeLabel ?? segmentCopy.homeLabel ?? DEFAULT_HOME_LABEL
  const resolvedTone = tone ?? segmentCopy.tone ?? DEFAULT_TONE

  // Next.js's `reset()` alone only re-renders the client tree; it does NOT
  // force server components in this segment to refetch. `router.refresh()`
  // re-runs the server-component data fetches for the current route without
  // a full page reload, so "Try again" doesn't silently re-show the same
  // stale data that caused the error in the first place. Order: refresh
  // before reset, so the refetch is already in flight when the boundary
  // clears — a fresh render then arrives via the refresh instead of briefly
  // re-rendering the stale tree.
  function handleReset() {
    router.refresh()
    reset()
  }

  // `route-error-boundary-card` marks the boundary on EVERY real route, so an
  // e2e spec asserting it absent proves the page did not land here (#14453).
  return (
    <ErrorBoundaryCard
      testId="route-error-boundary-card"
      tone={resolvedTone}
      title={resolvedTitle}
      message={resolvedMessage}
      homeHref={resolvedHomeHref}
      homeLabel={resolvedHomeLabel}
      error={error}
      onReset={isChunkError ? safeRefresh : handleReset}
      resetLabel={isChunkError ? CHUNK_LOAD_RESET_LABEL : undefined}
      onCopyFailure={err => {
        toastError({
          message: 'Could not copy error details to clipboard.',
          context: { action: `${segment}.error_boundary.copy`, error: err },
        })
      }}
    />
  )
}
